import {type ExtendedModInfo} from './modsDetector';
import {getFooterHtml, getLogs, getReportHtml} from './errorPrinter';
import {getDetailedName, sleep} from './utils';
import {getErrorContext} from './errorContext';
import Alert from '@libs/sweetalert';
import logger from './logging';

function restart(): never {
    location.reload();
    logger.info('Restarting...');
    throw new Error('Restart');
}

function wrapJson(report: string) {
    const maxJsonMessageLength = 1900;
    if (report.length > maxJsonMessageLength) {
        return report;
    }
    return '```json\n' + report + '\n```';
}

export async function showMissingModError(mod: ExtendedModInfo, mods: ExtendedModInfo[]) {
    const getModName = (mod: ExtendedModInfo) => mod.parameters.displayedName || mod.name;

    const dependentMods = (mod.requiredBy ?? []).map((name) => {
        const dependencyInfo = mods.filter((m) => m.name === name)[0];
        const dependencyName = getModName(dependencyInfo) || name;
        return `<pre><b>${dependencyName}</b></pre>`;
    });

    // TODO: Search mod in steam.
    await Alert.fire({
        title: `Failed to load mod '${getModName(mod)}'`,
        html: `
            Install mod ${getDetailedName(mod)}.<br>
            Following mods depend on it: ${dependentMods.join("\n")}
            Make sure to read mod's README ("Requirements"&nbsp;section) and install all requirements.
        `,
        icon: 'error',
        confirmButtonText: 'Restart',
        allowOutsideClick: false,
        showConfirmButton: true,
    });

    return restart();
}

export async function showFatalError(error: any) {
    if (error) {
        logger.error({error}, 'Fatal error');
    }

    if (typeof SceneManager !== 'undefined') {
        SceneManager.stop();
    }

    const reportHtml = getReportHtml(error);
    const footer = getFooterHtml();

    do {
        if (Alert.isVisible()) {
            await sleep(500);
            continue;
        }

        const result = await Alert.fire({
            icon: 'error',
            title: `Unexpected error`,
            html: reportHtml,
            footer: footer,
            cancelButtonText: 'Copy report',
            confirmButtonText: 'Restart',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCancelButton: true,
            showConfirmButton: true,
        });

        if (result.isDismissed && result.dismiss === Alert.DismissReason.cancel) {
            const indentSize = 2;
            const logsText = await getLogs(indentSize);

            const getSerializableError = (err: Error) => {
                return {
                    message: err.toString(),
                    stack: (err.stack || '').split('\n')
                }
            }

            const errorText = JSON.stringify(
                {
                    ...getErrorContext(),
                    error: getSerializableError(error)
                },
                null,
                indentSize
            );

            const report = errorText.slice(0, -1) +
                logsText + ',\n' +
                errorText[errorText.length - 1];

            const json = wrapJson(report);
            try {
                const clipboard = nw.Clipboard.get();
                clipboard.set(json, 'text');
            } catch {
                await navigator.clipboard.writeText(json);
            }
        }

        if (result.isConfirmed) {
            return restart();
        }
    } while (true);
}
